import { InfoCircledIcon, LockClosedIcon } from '@radix-ui/react-icons';
import { HTMLAttributes, PropsWithChildren } from 'react';

import { VariantProps } from 'class-variance-authority';
import { Badge, badgeVariants } from '../../../primitives/badge';
import { Button } from '../../../primitives/button';
import { FormControl, FormItem, FormMessage } from '../../../primitives/form';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../primitives/tooltip';
import { FormLabelWithoutRef } from '../../label/label-without-ref';
import { Control, CONTROL } from '../constants/control';
import { DATA_TYPE } from '../constants/data-type';
import { Attribute } from './attribute';

interface FieldWrapperProps
  extends
    Pick<HTMLAttributes<HTMLDivElement>, 'className'>,
    Pick<
      Attribute,
      | 'button'
      | 'controlType'
      | 'dataType'
      | 'dependencyMessage'
      | 'description'
      | 'id'
      | 'returnAllFieldsMessage'
      | 'label'
      | 'labelExtra'
      | 'optionalLabel'
      | 'tooltip'
    > {
  badge?: {
    text: string;
    variant?: VariantProps<typeof badgeVariants>['variant'];
    isHidden?: boolean;
  };
}

export function FieldWrapper({
  badge,
  button,
  children,
  className,
  controlType,
  dataType,
  dependencyMessage,
  description,
  id: baseId,
  returnAllFieldsMessage,
  label,
  labelExtra,
  optionalLabel,
  tooltip,
}: PropsWithChildren<FieldWrapperProps>) {
  return (
    <FormItem className={className}>
      <div>
        {label ? (
          <div className="flex flex-wrap items-center gap-y-1">
            <FormLabelWithoutRef>
              {`${label?.charAt(0)?.toUpperCase()}${label?.slice(1)}`}
              {optionalLabel ? (
                <span className="ml-1 text-[0.75rem] text-muted-foreground">opcional</span>
              ) : null}
            </FormLabelWithoutRef>
            {tooltip?.title || tooltip?.content ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger tabIndex={-1} className="ml-2">
                    <InfoCircledIcon className="size-4 text-accent" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-72 p-4">
                    <p className="pb-1 font-medium">{tooltip?.title}</p>
                    <div className="text-muted-foreground">{tooltip?.content}</div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
            {badge ? (
              badge.isHidden ? null : (
                <Badge className="ml-2" size="sm" variant={badge.variant ?? 'success'}>
                  {badge.text}
                </Badge>
              )
            ) : (
              <BadgeType controlType={controlType} dataType={dataType} />
            )}
            {labelExtra}
            {(controlType === CONTROL.radio ||
              controlType === CONTROL.select ||
              controlType === CONTROL.number ||
              controlType === CONTROL.telephone) &&
            button?.onClick &&
            !dependencyMessage ? (
              <Button
                id={button?.id}
                className="m-0 ml-4 p-0"
                variant="link"
                size="sm"
                onClick={button.onClick}
              >
                {button?.icon ? button.icon : null}
                {button?.text ? button.text : null}
              </Button>
            ) : null}
          </div>
        ) : null}
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        {returnAllFieldsMessage ? (
          <p className="mt-1 text-xs text-accent">{returnAllFieldsMessage}</p>
        ) : null}
      </div>
      <FormControl>
        {dependencyMessage ? (
          <div
            role="status"
            aria-disabled="true"
            className="flex items-start gap-2 rounded-lg border border-dashed border-input bg-muted/30 px-3 py-3 text-sm text-muted-foreground"
          >
            <LockClosedIcon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            <div>
              <p className="font-medium">{dependencyMessage}</p>
              <p className="mt-1 text-xs">
                Las opciones se cargarán automáticamente al completar estos campos.
              </p>
            </div>
          </div>
        ) : (
          children
        )}
      </FormControl>
      <FormMessage id={baseId ? `${baseId}-formMessage` : undefined} />
    </FormItem>
  );
}

const CONTROLS_WITH_BADGE: Control[] = [
  CONTROL.select,
  CONTROL.radio,
  CONTROL.multiSelector,
  CONTROL.checkbox,
];

function BadgeType({ controlType, dataType }: Pick<Attribute, 'controlType' | 'dataType'>) {
  if (!CONTROLS_WITH_BADGE.includes(controlType)) return null;

  return (
    <Badge className="ml-2" size="sm" variant="success">
      {dataType === DATA_TYPE.number || dataType === DATA_TYPE.arrayNumber ? 'number' : 'string'}
    </Badge>
  );
}
